/**
 * The add-in's transport for the shared oraChat panel (outlook-orachat-uplift
 * s4, proof K2; design C3, C5, C9).
 *
 * The panel calls `oraChatFetch` with the Remix route literals Ora web uses.
 * Here those become the Product API's bearer-authenticated
 * `OraChatProxyController` routes (`/api/workflows/orachat/*`), reached
 * same-origin through the host relay (US28491) or through `apiBase` on the
 * proof harness's cross-origin path.
 *
 * CLOSED: a path not in the table never reaches the network. Two are answered
 * here (plan push-channel → 503, which `usePlanSignalR` treats as "poll
 * instead"; speech ack → 204). Everything else — the document store and the
 * vault included (C50) — answers `transport_unmapped_route`; a fall-through
 * would hit the add-in's own origin, whose SPA fallback answers index.html 200.
 *
 * Every mapped call carries the bearer, `X-Ora-Surface: outlook_addin` and a
 * correlation id (header names from config/oraHeaders.ts) and never a cookie.
 * Nothing is retried: ask is not idempotent (`client_send_id` is its dedupe
 * key). A fetch `TypeError` — how a CORS rejection or a dead network shows up
 * in the browser, with nothing logged on the server — becomes a named
 * `network_or_cors` error that records the headers sent.
 *
 * No MSAL here: the caller passes the token getter (the silent
 * `getProductApiTokenSilent`, installed at boot by `bootOraChat`).
 */

import firefly from "../assets/firefly.svg";
import {
  setOraChatAvatarSrc,
  setOraChatTransport,
  type OraChatTransport,
} from "@product-app/features/oraChatV2/transport";

import { getApiBaseUrl } from "../config/apiBaseUrl";
import { ORA_ADDIN_HEADERS, ORA_ADDIN_VOICE_HOST_TAG } from "../config/oraHeaders";
import { logger, newCorrelationId } from "../obs/logger";
import { SignInRequiredError } from "../services/authClient";
import { addinCallHeaders, addinFetch } from "./addinRequest";
import { ADDIN_WINDOW_INSTANCE } from "./windowInstance";

/** Panel paths that carry the voice host and this window's id. Chat routes do not. */
const ADDIN_VOICE_ROUTES = new Set<string>([
  "/resources/orachat/voice/stt/api",
  "/resources/orachat/voice/realtime/session/api",
]);

const API = "/api/workflows/orachat";

type RouteMap = ReadonlyArray<readonly [RegExp, (m: RegExpMatchArray) => string]>;

/** Remix literal the panel calls → Product API route (OraChatProxyController.cs). */
export const ADDIN_ORACHAT_ROUTES: RouteMap = [
  [/^\/resources\/orachat\/ask\/api$/, () => `${API}/ask`],
  [/^\/resources\/orachat\/continue\/api$/, () => `${API}/continue`],
  [/^\/resources\/orachat\/abort\/api$/, () => `${API}/abort`],
  [/^\/resources\/chat\/prefs\/api$/, () => `${API}/prefs`],
  [/^\/resources\/chat\/sessions\/api$/, () => `${API}/sessions`],
  [
    /^\/resources\/chat\/sessions\/by-conversation\/([^/]+)\/messages\/api$/,
    (m) => `${API}/sessions/by-conversation/${m[1]}/messages`,
  ],
  [/^\/resources\/chat\/sessions\/([^/]+)\/title\/api$/, (m) => `${API}/sessions/${m[1]}/title`],
  [/^\/resources\/chat\/sessions\/([^/]+)\/pin\/api$/, (m) => `${API}/sessions/${m[1]}/pin`],
  [/^\/resources\/chat\/sessions\/([^/]+)\/api$/, (m) => `${API}/sessions/${m[1]}`],
  [/^\/resources\/orachat\/plan\/([^/]+)\/api$/, (m) => `${API}/plan/${m[1]}`],
  [/^\/resources\/orachat\/voice\/stt\/api$/, () => `${API}/voice/stt`],
  // Mint, heartbeat and release all POST here (the body's `action` differs).
  [/^\/resources\/orachat\/voice\/realtime\/session\/api$/, () => `${API}/voice/realtime/session`],
];

/** Panel paths answered here, never sent. */
const LOCAL_ANSWERS: ReadonlyArray<readonly [RegExp, () => Response]> = [
  // The .NET shape is POST .../negotiate; 503 is the plan hook's "poll instead".
  [
    /^\/api\/orachat\/plan\/[^/]+\/push-channel$/,
    () => jsonResponse(503, { error: "push_channel_unavailable" }),
  ],
  // Fire-and-forget acknowledgement of a spoken reply.
  [/^\/resources\/orachat\/voice\/speech_ack\/api$/, () => new Response(null, { status: 204 })],
];

/** The Product API path for a panel path, or null when the table has none. */
export function mapOraChatPath(pathname: string): string | null {
  for (const [re, to] of ADDIN_ORACHAT_ROUTES) {
    const m = pathname.match(re);
    if (m) return to(m);
  }
  return null;
}

/** What a mapped call's fetch failed with before any response arrived. */
export class OraChatTransportError extends Error {
  readonly code = "network_or_cors" as const;
  constructor(
    readonly target: string,
    /** The header names that were sent — a CORS preflight rejects on these. */
    readonly headers: string[],
    readonly correlationId: string,
    cause: unknown,
  ) {
    super(
      `network_or_cors: ${target} failed before any response ` +
        `(headers sent: ${headers.join(", ")}; correlation id ${correlationId})`,
      { cause },
    );
    this.name = "OraChatTransportError";
  }
}

export interface AddinOraChatTransportOptions {
  /** The Product API bearer (the add-in passes the silent `getProductApiTokenSilent`). */
  getToken: () => Promise<string>;
  /** Default: `getApiBaseUrl()` at call time ("" = same-origin through the host relay). */
  apiBase?: string;
  /** Defaults to the platform fetch, looked up per call. */
  fetchImpl?: typeof fetch;
  /**
   * Called when `getToken` throws `SignInRequiredError` (the silent token is
   * gone and only an interactive sign-in can get a new one), just before the
   * error is rethrown to the panel. The host shows its sign-in card.
   */
  onSignInRequired?: () => void;
}

function jsonResponse(status: number, body: Record<string, unknown>): Response {
  return new Response(JSON.stringify(body), {
    status,
    headers: { "Content-Type": "application/json" },
  });
}

function pathOf(input: RequestInfo | URL): URL {
  const raw =
    typeof input === "string" ? input : input instanceof URL ? input.href : input.url;
  // A path-only literal resolves against a placeholder origin; an absolute URL
  // keeps its own pathname and can only match the table by that pathname.
  return new URL(raw, "http://orachat-panel.invalid");
}

export function createAddinOraChatTransport({
  getToken,
  apiBase,
  fetchImpl,
  onSignInRequired,
}: AddinOraChatTransportOptions): OraChatTransport {
  const send = addinFetch(fetchImpl);

  return async (input, init) => {
    const url = pathOf(input);
    const local = LOCAL_ANSWERS.find(([re]) => re.test(url.pathname));
    if (local) return local[1]();

    const correlationId = newCorrelationId();
    const route = mapOraChatPath(url.pathname);
    if (!route) {
      logger.warn("oraChat transport: unmapped panel route answered locally", {
        correlation_id: correlationId,
        path: url.pathname,
        method: init?.method ?? "GET",
      });
      return jsonResponse(404, {
        error: "transport_unmapped_route",
        path: url.pathname,
        correlation_id: correlationId,
      });
    }

    let token: string;
    try {
      token = await getToken();
    } catch (err) {
      if (err instanceof SignInRequiredError) onSignInRequired?.();
      throw err;
    }
    const headers = new Headers(init?.headers);
    for (const [name, value] of Object.entries(addinCallHeaders(token, correlationId))) {
      headers.set(name, value);
    }
    if (ADDIN_VOICE_ROUTES.has(url.pathname)) {
      headers.set(ORA_ADDIN_HEADERS.oraChatHost, ORA_ADDIN_VOICE_HOST_TAG);
      headers.set(ORA_ADDIN_HEADERS.surfaceInstance, ADDIN_WINDOW_INSTANCE);
    }
    const target = `${apiBase ?? getApiBaseUrl()}${route}${url.search}`;

    try {
      // One attempt, always (C9). `credentials: "omit"` overrides the panel's
      // `include`: the bearer is the identity, never a cookie.
      return await send(target, { ...init, headers, credentials: "omit" });
    } catch (err) {
      if (!(err instanceof TypeError)) throw err; // AbortError and the like stay as they are
      const sent: string[] = [];
      headers.forEach((_value, name) => sent.push(name));
      logger.warn("oraChat transport: request failed before any response", {
        correlation_id: correlationId,
        target,
        headers: sent.join(","),
      });
      throw new OraChatTransportError(target, sent, correlationId, err);
    }
  };
}

/**
 * Install the add-in transport and the add-in's bundled avatar into the
 * shared panel. Returns the uninstall (both cleared).
 */
export function installAddinOraChatTransport(opts: AddinOraChatTransportOptions): () => void {
  setOraChatTransport(createAddinOraChatTransport(opts));
  setOraChatAvatarSrc(firefly);
  return () => {
    setOraChatTransport(null);
    setOraChatAvatarSrc(null);
  };
}
